import { useEffect, useMemo, useState } from 'react';
import { GetCustomerDataResponse, LocationModel } from '../../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import { StoneXButton, StoneXCheckbox } from '../../../../StoneXMui';
import { readFuturesAllocationSettings, writeFuturesAllocationSettings } from '../../Dashboard/FuturesAllocationSettings';
import { BaseReportSettingsPageProps } from './types';

/**
 * Settings for the dashboard's Futures Allocation report.
 *
 * The report assumes futures are bought at a parent location and rolled down to a child
 * through an exchange for physical. Children that instead trade their own futures and
 * swaps are ticked here, and the report counts their hedges in the parent's table as
 * well as on the child's own card.
 *
 * The settings are stored inside the site's Position report configuration, so there is
 * no new report type and no backend change; see FuturesAllocationSettings.ts.
 */
export default function FuturesAllocationReportSettingsPage({ report, isSaving, onUpdateReport }: BaseReportSettingsPageProps) {
  const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse | undefined => state.commodityManagement.customerData);
  const locations = useMemo(() => customerData?.locations ?? [], [customerData?.locations]);

  const savedSettings = useMemo(() => readFuturesAllocationSettings(report.configuration), [report.configuration]);
  const [locationIdsWithoutEfp, setLocationIdsWithoutEfp] = useState<number[]>(savedSettings.locationIdsWithoutEfp);

  useEffect(() => {
    setLocationIdsWithoutEfp(savedSettings.locationIdsWithoutEfp);
  }, [savedSettings]);

  // Every location that has a parent, grouped under that parent in tier order.
  const groups = useMemo(() => {
    const byId = new Map<number, LocationModel>(locations.map(l => [l.locationId, l]));
    const parents = locations
      .filter(parent => locations.some(child => child.parentLocationId === parent.locationId))
      .sort((a, b) => (a.tierRank ?? 0) - (b.tierRank ?? 0) || a.locationName.localeCompare(b.locationName));

    return parents.map(parent => ({
      parent,
      children: locations
        .filter(child => child.parentLocationId === parent.locationId)
        .sort((a, b) => a.locationName.localeCompare(b.locationName)),
      grandParentName: parent.parentLocationId != null ? byId.get(parent.parentLocationId)?.locationName : undefined,
    }));
  }, [locations]);

  const isDirty = useMemo(() => {
    const saved = [...savedSettings.locationIdsWithoutEfp].sort((a, b) => a - b).join(',');
    const current = [...locationIdsWithoutEfp].sort((a, b) => a - b).join(',');
    return saved !== current;
  }, [savedSettings, locationIdsWithoutEfp]);

  function toggle(locationId: number, checked: boolean) {
    setLocationIdsWithoutEfp(current => (checked ? Array.from(new Set([...current, locationId])) : current.filter(id => id !== locationId)));
  }

  function reset() {
    setLocationIdsWithoutEfp(savedSettings.locationIdsWithoutEfp);
  }

  function save() {
    onUpdateReport({
      ...report,
      configuration: writeFuturesAllocationSettings(report.configuration, { locationIdsWithoutEfp }),
    });
  }

  return (
    <div>
      <StoneXRow>
        <h3>Futures Allocation</h3>
      </StoneXRow>

      <StoneXRow>
        <p style={{ margin: 0 }}>
          The Futures Allocation report on the dashboard assumes futures are bought at the parent location and moved to a child
          location through an exchange for physical when the physical is bought. Tick any location that trades its own futures
          and swaps instead; the report will count those hedges in the parent location's table as well as on the child's
          own card.
        </p>
      </StoneXRow>

      <StoneXDivider />

      {groups.length === 0 && (
        <StoneXRow>
          <p>No locations with a parent were found for this site.</p>
        </StoneXRow>
      )}

      {groups.map(({ parent, children, grandParentName }) => (
        <div key={parent.locationId} style={{ marginBottom: 16 }}>
          <StoneXRow>
            <h4 style={{ margin: 0 }}>
              {parent.locationName}
              {parent.tierName ? <span style={{ fontWeight: 400, color: '#757575' }}> &middot; {parent.tierName}</span> : null}
              {grandParentName ? <span style={{ fontWeight: 400, color: '#757575' }}> &middot; under {grandParentName}</span> : null}
            </h4>
          </StoneXRow>
          {children.map(child => (
            <StoneXRow key={child.locationId}>
              <StoneXCheckbox
                label={child.locationName}
                helperText={
                  locationIdsWithoutEfp.includes(child.locationId)
                    ? `Trades its own futures and swaps. Also counted with ${parent.locationName}.`
                    : `Receives futures from ${parent.locationName} through exchange for physical.`
                }
                checked={locationIdsWithoutEfp.includes(child.locationId)}
                onUpdate={checked => toggle(child.locationId, checked)}
              />
            </StoneXRow>
          ))}
        </div>
      ))}

      <StoneXRow justify="end" marginDirection="top">
        <StoneXButton variant="outlined" onClick={reset} disabled={!isDirty}>
          Reset
        </StoneXButton>
        <StoneXButton isWorking={isSaving} onClick={save} disabled={!isDirty}>
          Save
        </StoneXButton>
      </StoneXRow>
    </div>
  );
}
